'use client'

import { Calculator, Loader2, BarChart3 } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import Link from '@/modules/shared/components/Link'
import CountrySelector from '@/modules/hs/components/common/CountrySelector'
import { api } from '@/modules/hs/api/api'
import { taxCalculator } from '@/modules/hs/api/hs-tax.api'
import { TaxCalculatorResponse } from '@/modules/hs/types/tax.types'
import { formatCurrency } from '@/modules/shared/utils/number.util'
import posthog from 'posthog-js'
import { useAuth } from '@/modules/auth/contexts/AuthContext'

const EXCHANGE_RATE = 25000
const VAT_RATE = 10

interface QuickTaxResult {
  goodsValueVND: number
  taxNK: number
  taxNKRate: number
  taxNKName: string
  taxTTDB: number
  taxBVMT: number
  taxVAT: number
  totalTax: number
  totalAmount: number
}

export default function CalculatorTeaserSection() {
  const { user } = useAuth()
  const [formData, setFormData] = useState({
    hsCode: '',
    importValue: '',
    country: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<QuickTaxResult | null>(null)
  const [apiError, setApiError] = useState<string | null>(null)

  const sectionRef = useRef<HTMLElement>(null)
  const trackedAttentionRef = useRef(false)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const element = sectionRef.current
    if (!element) return

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries

        if (entry.isIntersecting && !trackedAttentionRef.current) {
          // Bắt đầu đếm 5s
          if (!timerRef.current) {
            timerRef.current = setTimeout(() => {
              trackedAttentionRef.current = true
              posthog.capture('calculator_teaser_viewed', {
                user_id: user?.email || 'anonymous',
                page_url: window.location.href,
                page_path: window.location.pathname,
                duration_seconds: 5,
              })

              if (observer) observer.disconnect()
            }, 5000)
          }
        } else {
          // Nếu user cuộn đi chỗ khác trước 5s thì huỷ timer đếm
          if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
          }
        }
      },
      { threshold: 1 }
    )

    observer.observe(element)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      observer.disconnect()
    }
  }, [user?.email])

  // Format number string for input field display (e.g. '1000' → '1,000')
  const formatInputNumber = (value: string) => {
    const num = value.replace(/[^\d]/g, '')
    return num.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }

  // Parse formatted number back to number
  const parseFormattedNumber = (value: string) => {
    return parseFloat(value.replace(/,/g, '')) || 0
  }

  // Validate HS code (minimum 8 digits)
  const validateHsCode = (code: string) => {
    const cleanCode = code.replace(/\D/g, '')
    if (cleanCode.length < 8) {
      return 'Mã HS phải có tối thiểu 8 chữ số'
    }
    return null
  }

  // Check if HS code exists in database
  const checkHsCodeExists = async (hsCode: string): Promise<boolean> => {
    try {
      const response = await api.get(`/hscode/${hsCode}`)
      console.log("🚀 > checkHsCodeExists > response:", response.data)
      return response.data && response.data.hs
    } catch {
      return false
    }
  }

  // Calculate tax based on taxCalculator response
  const calculateQuickTax = (
    importValueUSD: number,
    taxData: TaxCalculatorResponse
  ): QuickTaxResult => {
    const goodsValueVND = importValueUSD * EXCHANGE_RATE

    // Chọn thuế NK (ưu tiên NKUDDB > NKUD > NKTT)
    let taxNKRate = 0
    let taxNKName = 'Thuế nhập khẩu'

    if (taxData.taxNKUDDB !== undefined && taxData.taxNKUDDB !== null && taxData.taxNKUDDB >= 0) {
      taxNKRate = taxData.taxNKUDDB
      taxNKName = 'Thuế NK ưu đãi đặc biệt (FTA)'
    } else if (taxData.taxNKUD !== undefined && taxData.taxNKUD !== null) {
      taxNKRate = taxData.taxNKUD
      taxNKName = 'Thuế NK ưu đãi'
    } else if (taxData.taxNKTT !== undefined && taxData.taxNKTT !== null) {
      taxNKRate = taxData.taxNKTT
      taxNKName = 'Thuế NK thông thường'
    }

    if (taxNKRate < 0) taxNKRate = 0

    const taxNK = goodsValueVND * (taxNKRate / 100)
    const afterNK = goodsValueVND + taxNK

    // Thuế TTDB
    const ttdbRate = taxData.taxTTDB ?? 0
    const taxTTDB = afterNK * (ttdbRate / 100)
    const afterTTDB = afterNK + taxTTDB

    // Thuế BVMT (tuyệt đối - giả sử quantity = 1)
    const taxBVMT = taxData.taxBVMT ?? 0

    // Thuế VAT
    const taxVAT = (afterTTDB + taxBVMT) * (VAT_RATE / 100)

    const totalTax = taxNK + taxTTDB + taxBVMT + taxVAT
    const totalAmount = goodsValueVND + totalTax

    return {
      goodsValueVND,
      taxNK,
      taxNKRate,
      taxNKName,
      taxTTDB,
      taxBVMT,
      taxVAT,
      totalTax,
      totalAmount,
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setApiError(null)
    setResult(null)

    // Validate form
    const newErrors: Record<string, string> = {}

    const hsCodeError = validateHsCode(formData.hsCode)
    if (hsCodeError) newErrors.hsCode = hsCodeError

    if (!formData.importValue || parseFormattedNumber(formData.importValue) <= 0) {
      newErrors.importValue = 'Vui lòng nhập giá trị nhập khẩu'
    }

    if (!formData.country) {
      newErrors.country = 'Vui lòng chọn quốc gia'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setErrors({})
    setIsLoading(true)

    try {
      // Check if HS code exists
      const exists = await checkHsCodeExists(formData.hsCode)
      console.log("🚀 > handleSubmit > exists:", exists)
      if (!exists) {
        setErrors({ hsCode: 'Mã HS không tồn tại trong hệ thống' })
        setIsLoading(false)
        return
      }

      // Get tax data
      const today = new Date().toISOString().split('T')[0]
      const taxData = await taxCalculator(formData.hsCode, formData.country, today)

      // Calculate result
      const importValueUSD = parseFormattedNumber(formData.importValue)
      const calculatedResult = calculateQuickTax(importValueUSD, taxData)
      setResult(calculatedResult)
    } catch (error) {
      console.error('Error calculating tax:', error)
      setApiError('Không thể tính thuế. Vui lòng thử lại.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <section ref={sectionRef} className="calculator-teaser-section rounded-xl">
      <div className="calculator-teaser-content">
        <div className="calculator-teaser-header">
          <span className="section-badge section-bold-blue">TÍNH NĂNG NỔI BẬT</span>
          <h2 className="calculator-teaser-title">
            Tính thuế nhập khẩu trong 30 giây
          </h2>
          <p className="calculator-teaser-subtitle">
            Tận dụng triệt để mức thuế nhập khẩu về mức tối thiểu 0% từ 17+ Hiệp định FTA.
            Nhập thông tin cơ bản để ước tính dễ dàng, chính xác
          </p>
        </div>

        <form className="calculator-form cl-card-border" onSubmit={handleSubmit}>
          <div className="calculator-inputs">
            <div className="calculator-input-group">
              <label className="calculator-label">
                Mã HS
              </label>
              <input
                type="text"
                className={`calculator-input ${errors.hsCode ? 'border-red-500' : ''}`}
                placeholder="Ví dụ: 09011100"
                value={formData.hsCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10)
                  setFormData({ ...formData, hsCode: value })
                  if (errors.hsCode) setErrors({ ...errors, hsCode: '' })
                }}
              />
              {errors.hsCode && (
                <span className="text-red-500 text-sm mt-1">{errors.hsCode}</span>
              )}
            </div>
            <div className="calculator-input-group">
              <label className="calculator-label">
                Giá trị nhập khẩu (USD)
              </label>
              <input
                type="text"
                className={`calculator-input ${errors.importValue ? 'border-red-500' : ''}`}
                placeholder="Ví dụ: 10,000"
                value={formData.importValue}
                onChange={(e) => {
                  const formatted = formatInputNumber(e.target.value)
                  setFormData({ ...formData, importValue: formatted })
                  if (errors.importValue) setErrors({ ...errors, importValue: '' })
                }}
              />
              {errors.importValue && (
                <span className="text-red-500 text-sm mt-1">{errors.importValue}</span>
              )}
            </div>
            <div className="calculator-input-group">
              <label className="calculator-label">
                Quốc gia xuất khẩu
              </label>
              <CountrySelector
                value={formData.country}
                onValueChange={(value) => {
                  setFormData({ ...formData, country: value })
                  if (errors.country) setErrors({ ...errors, country: '' })
                }}
                placeholder="Chọn quốc gia"
                className="w-full h-auto py-3 px-4"
                error={!!errors.country}
              />
              {errors.country && (
                <span className="text-red-500 text-sm mt-1">{errors.country}</span>
              )}
            </div>
          </div>
          <button
            type="submit"
            className="calculator-submit"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Đang tính...
              </>
            ) : (
              <>
                <Calculator size={20} />
                Tính ngay
              </>
            )}
          </button>
        </form>

        {apiError && (
          <div className="calculator-result bg-red-50 border-red-200">
            <div className="text-red-600">{apiError}</div>
          </div>
        )}

        {result && (
          <div className="calculator-result">
            <div className="calculator-result-row">
              <div className="calculator-result-main">
                <BarChart3 size={20} className="text-primary" />
                <span className="result-label">Kết quả ước tính:</span>
                <span className="result-value">
                  {formatCurrency(result.totalTax)} VNĐ
                </span>
              </div>
              <Link
                href={`/chi-tiet-ma-hs/${formData.hsCode}`}
                className="calculator-detail-btn "
              >
                Xem chi tiết
              </Link>
            </div>
            <p className="calculator-result-note">
              <em>
                Lưu ý: Kết quả ước tính áp dụng đối với hàng hóa đáp ứng điều kiện được hưởng thuế NK ưu đãi đặc biệt tại thời điểm tính. Kết quả này chưa tính đến các trường hợp miễn, giảm và hoàn thuế; thuế tự vệ, chống bán phá giá và trợ cấp, các loại thuế khác như TTĐB, BVMT, Thuế nhà thầu. Bảng tính trên chỉ mang tính chất tham khảo và không phải là ý kiến tư vấn cho bất kỳ trường hợp cụ thể nào.
              </em>
            </p>
          </div>
        )}
      </div>
    </section>
  )
}
